import React, { FC, useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  RouteComponentProps,
  Link as RouterLink,
  navigate,
} from '@reach/router';

import Body from '@bitcoin-portal/verse-web-components/dist/Body';
import Button from '@bitcoin-portal/verse-web-components/dist/Button';
import { Card } from '@bitcoin-portal/verse-web-components/dist/Card';
import Coin from '@bitcoin-portal/verse-web-components/dist/Coin';
import CoinPair from '@bitcoin-portal/verse-web-components/dist/CoinPair';
import H2 from '@bitcoin-portal/verse-web-components/dist/H2';
import Label from '@bitcoin-portal/verse-web-components/dist/Label';
import Title from '@bitcoin-portal/verse-web-components/dist/Title';
import { colors } from '@bitcoin-portal/verse-web-components/dist/themes/colors';

import { useFarms } from '@context/providers/FarmsProvider';
import { useTrackedState } from '@context/store';

import { findExternalIcon, findProvider } from '@helpers/utils';

import {
  CoinWrapper,
  DetailHeader,
  DetailsWrapper,
  LabelWrapper,
  MessageWrapper,
  NoDetailsSummary,
  StyledChevron,
} from './styled';

export const NoDetails: FC<RouteComponentProps> = () => {
  const { provider: exchangeProvider, selectedLiquidityPair } =
    useTrackedState();

  const { selectedFarm } = useFarms();

  const provider = findProvider(exchangeProvider);
  const pair = selectedLiquidityPair;

  const tickerA = useMemo(
    () => selectedLiquidityPair.token0?.symbol || '',
    [selectedLiquidityPair],
  );
  const tickerB = useMemo(
    () => selectedLiquidityPair.token1?.symbol || '',
    [selectedLiquidityPair],
  );

  const chainParam = useMemo(() => {
    return provider.chainParam || 'eth';
  }, [provider]);

  const pairParam = useMemo(() => {
    return `${tickerA.toLowerCase()}-${tickerB.toLowerCase()}`;
  }, [tickerA, tickerB]);

  const goBack = useCallback(() => {
    navigate(`/farms/${chainParam}/`);
  }, [chainParam]);

  return (
    <>
      <DetailsWrapper>
        <DetailHeader>
          <StyledChevron onClick={goBack} />
          <CoinPair
            coinA={{
              ticker: tickerA,
              altSrc: findExternalIcon(
                pair?.token0?.id,
                tickerA,
                exchangeProvider,
              ),
              size: '44px',
            }}
            coinB={{
              ticker: tickerB,
              altSrc: findExternalIcon(
                pair?.token1?.id,
                tickerB,
                exchangeProvider,
              ),
              size: '44px',
            }}
          />
          <H2 style={{ padding: '16px 0 8px' }}>
            {tickerA}/{tickerB}
          </H2>
        </DetailHeader>
        <NoDetailsSummary>
          <Card>
            <LabelWrapper>
              <Label style={{ color: colors.shade80 }}>
                <FormattedMessage id="farms.noDetails.apy" />
              </Label>
            </LabelWrapper>
            <H2>{selectedFarm?.apy || '...'}%</H2>
          </Card>
          <Card>
            <LabelWrapper>
              <Label style={{ color: colors.shade80 }}>
                <FormattedMessage id="farms.noDetails.rewards" />
              </Label>
            </LabelWrapper>
            <CoinWrapper>
              <Coin
                alt={selectedFarm?.rewardToken?.ticker || 'verse'}
                altSrc={selectedFarm?.rewardToken?.icon}
                ticker={selectedFarm?.rewardToken?.ticker || 'verse'}
                size="24"
              />
              <Title>{selectedFarm?.rewardToken?.abbr || 'VERSE'}</Title>
            </CoinWrapper>
          </Card>
        </NoDetailsSummary>

        <MessageWrapper>
          <Card>
            <div
              style={{
                display: 'flex',
                flexFlow: 'row nowrap',
                alignItems: 'flex-start',
                marginBottom: 16,
              }}
            >
              <Coin ticker="verse-x" alt="verse-x" size="44px" />
              <div style={{ paddingLeft: 16 }}>
                <Title>
                  <FormattedMessage id="farms.noDetails.noTokens" />
                </Title>
                <Body
                  size="medium"
                  style={{
                    color: colors.shade80,
                    maxWidth: 410,
                    paddingTop: 8,
                  }}
                >
                  <FormattedMessage
                    id="farms.noDetails.message"
                    values={{
                      PAIR: `${tickerA}/${tickerB}`,
                    }}
                  />
                </Body>
              </div>
            </div>
            <RouterLink to={`/pools/${chainParam}/${pairParam}/`}>
              <Button small fullWidth as="div">
                <FormattedMessage
                  id="farms.noDetails.routesPoolButton"
                  values={{
                    PAIR: `${tickerA}/${tickerB}`,
                  }}
                />
              </Button>
            </RouterLink>
          </Card>
        </MessageWrapper>
      </DetailsWrapper>
    </>
  );
};

export default NoDetails;
